<template>
	<div click.stop>
		<div class="modal is-active">
			<div class="modal-background" @click="cancel"></div>
			<div class="modal-card">
				<header class="modal-card-head">
					<p class="modal-card-title">
						{{ editFireProtection.id ? $t('add_fire_protection.edit') : $t('add_fire_protection.new') }} {{ $t('add_fire_protection.title') }}
					</p>
					<button class="delete" aria-label="close" @click="cancel"></button>
				</header>
				<section class="modal-card-body">
					<div class="columns is-mobile" style="margin-bottom: 12px;">
						<div class="field">
							<label class="label">{{ $t('add_fire_protection.name') }}</label>
							<div class="control">
								<input
									type="text"
									v-model="fireProtection.name"
									class="input"
									:placeholder="$t('add_fire_protection.name_placeholder')"
								/>
							</div>
						</div>
					</div>
					<div class="columns is-mobile" style="margin-bottom: 12px;">
						<div class="field has-multiselect column is-half">
							<label class="label">{{ $t('add_fire_protection.group') }}</label>
							<div class="control">
								<v-select
									class="panel-select narrow"
									v-model="chosenInventoryGroupId"
									:options="fireProtectionGroupOptions"
									track-by="value"
									:reduce="opt => Number(opt.value)" 
									label="text"
									:placeholder="$t('add_fire_protection.select_type')"
									:allowEmpty="false"
									:internalSearch="true"
									:showLabels="false"
									>
									<template slot="no-options">
										{{ $t('add_fire_protection.no_results') }}
									</template>

									<template slot="open-indicator" slot-scope="{ attributes }">
										<span class="caret" v-bind="attributes">
										<i class="fas fa-caret-down caret-inner"></i>
										</span>
									</template>
								</v-select>
							</div>
						</div>
						<div class="field has-multiselect column is-half">
							<label class="label">{{ $t('add_fire_protection.type') }}</label>
							<div class="control">
							<v-select
								class="panel-select narrow"
								v-model="fireProtection.inventoryType"  
								:options="typeOptions"                    
								label="label"
								track-by="key"
								:reduce="opt => opt.key"
								:placeholder="$t('add_fire_protection.select_type')"
								:allowEmpty="false"
								:internalSearch="true"
								:showLabels="false"
								>
								<template slot="no-options">
									{{ $t('add_fire_protection.no_results') }}
								</template>

								<template slot="option" slot-scope="{ option }">
									<span class="vselect-option">
									<div
										class="fireprotection-icon"
										:class="option.icon"
										style="margin-right:6px"
										>
										 </div>
									{{ option.label }}
									</span>
								</template>

								<template slot="selected-option" slot-scope="{ option }">
									<span class="vselect-selected">
									<div
										class="fireprotection-icon"
										:class="option.icon"
										style="margin-right:6px"
										>
										 </div>
									{{ option.label }}
									</span>
								</template>

								<template slot="open-indicator" slot-scope="{ attributes }">
									<span class="caret" v-bind="attributes">
									<i class="fas fa-caret-down caret-inner"></i>
									</span>
								</template>
								</v-select>
							</div>
						</div>
					</div>
					<div class="field">
						<label class="label">{{ $t('add_fire_protection.description') }}</label>
						<div class="control">
							<textarea
								v-model="fireProtection.description"
								class="textarea"
								rows="2"
								:placeholder="$t('add_fire_protection.description_placeholder')"
							/>
						</div>
					</div>
					<div class="field">
						<label class="label">{{ $t('add_fire_protection.recommendation') }}</label>
						<div class="control">
							<textarea
								v-model="fireProtection.recommendation"
								class="textarea"
								rows="2"
									:placeholder="$t('add_fire_protection.recommendation_placeholder')"
							/>
						</div>
					</div>

					<div class="field ">
						<label class="label">{{ $t('add_fire_protection.instruction') }}</label>
						<div class="control">
							<textarea
								v-model="fireProtection.instruction"
								class="textarea"
								rows="2"
								:placeholder="$t('add_fire_protection.instruction_placeholder')"
							/>
						</div>
					</div>
					<div class="field" v-if="$isAllowedTo('global_set_object_as_global')">
						<label class="label">{{ $t('add_fire_protection.global') }}</label>
						<SwitchButton v-model="globalVisibility" />
					</div>
				</section>
				<footer class="modal-card-foot">
					<button
						class="button is-dark"
						:class="isLoading ? 'is-loading' : ''"
						:disabled="disableSave"
						@click="save"
					>
						<span class="icon is-info"><i class="fas fa-save"/></span
						><span>{{ $t('add_fire_protection.save') }}</span>
					</button>
				</footer>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import iconMixin from '@/mixins/iconMixin.js';


import SwitchButton from '@/components/switchButton.vue';

export default {
  components: { SwitchButton },
  mixins: [iconMixin],
  props: { editFireProtection: Object, option:Object },
  data() {
    return {
      fireProtection: {
        name: '',
        description: '',
        recommendation: '',
        instruction: '',
        inventoryType: ''
      },
      isLoading: false,
      saveingError: false,
      companyOptions: [],
      chosenInventoryGroupId: -1, // -1 = "not selected"
      globalVisibility: false
    };
  },
  computed: {
    disableSave() {
      return !this.fireProtection.name;
    },
    ...mapGetters('companyStore', ['companies']),
    ...mapGetters('user', ['getActiveCompany', 'getActiveCustomer']),

    fireProtectionGroupIds() {
      return [0, 1, 2, 3, 4, 5];
    },

	fireProtectionGroupOptions() {
		return [
			{ value: -1, text: this.$t('add_fire_protection.not_selected') },
			...[0,1,2,3,4,5].map(id => ({ value: id, text: this.$t(`fire_protection_group.${id}`) }))
		];
	}
  },
  methods: {
    ...mapActions('companyStore', ['getCompanies']),
	save() {
		if (this.isLoading) return;

		const grpRaw = Number(this.chosenInventoryGroupId);
		const fireProtectionType =
			Number.isFinite(grpRaw) && grpRaw >= 0 ? grpRaw : -1;

		let inventoryKey = this.normalizeTypeBare(this.fireProtection.inventoryType);
		if (!inventoryKey) inventoryKey = 'other';

		const payload = {
			...this.fireProtection,
			inventoryType: inventoryKey,
			fireProtectionType,
			companyId: this.globalVisibility
			? null
			: (this.fireProtection.companyId ?? this.getActiveCompany?.id ?? null),
			customerId: this.globalVisibility
			? null
			: (this.fireProtection.customerId ?? this.getActiveCustomer?.id ?? null),
		};

		['description', 'recommendation', 'instruction'].forEach(k => {
			if (typeof payload[k] === 'string') {
			payload[k] = payload[k].trim();
			if (payload[k] === '') payload[k] = null;
			}
		});

		const nameTrim = (payload.name || '').trim();
		if (!nameTrim && this.globalVisibility) {
			const fallback = this.tTypeKey(inventoryKey);
			if (fallback) payload.name = fallback;
		} else {
			payload.name = nameTrim;
		}

		if (!this.globalVisibility && (!payload.companyId || !payload.customerId)) {
			console.warn('Missing companyId/customerId for non-global save');
			return;
		}


		this.$emit('save', payload);
		this.isLoading = true;
	},


    cancel() {
      this.$emit('cancel');
    },

	getTypeLabel(val) {
		const key = typeof val === 'string' ? val : val?.key;
		return key ? this.$t(`fire_protection_type.${key}`) : '';
	}
  },
  created() {
	if (this.editFireProtection) {
		Object.assign(this.fireProtection, this.editFireProtection);

		this.fireProtection.inventoryType =
		this.normalizeTypeBare(this.fireProtection.inventoryType) ||
		(this.typeOptions[0] && this.typeOptions[0].key) ||
		'other';

		const id = typeof this.editFireProtection.fireProtectionType === 'number'
		? this.editFireProtection.fireProtectionType
		: -1;
		this.chosenInventoryGroupId = this.fireProtectionGroupIds.includes(id) ? id : -1;
	} else {
		this.fireProtection.inventoryType =
		(this.typeOptions[0] && this.typeOptions[0].key) || 'other';
		this.chosenInventoryGroupId = -1;
	}

		this.globalVisibility = this.fireProtection.companyId === null;
	}
};
</script>

<style lang="scss" scoped>
.switch {
	margin-top: 0;
}
</style>
